import { ComponentProps, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Loading } from '@factorialco/deprecated-design-system'
import { F0EventCatcherProvider } from '@factorialco/f0-react'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import {
  CalendarArrowRight as CalendarArrowRightIcon,
  CheckDouble as CheckDoubleIcon,
  Warning as WarningIcon,
} from '@factorialco/f0-react/icons/app'
import {
  InboxTaskListItemCompletionFragment,
  InboxTaskListItemFragment,
  NewInboxTasksDocument,
  NewInboxTasksQuery,
  TasksValueObjectsTaskCategoryEnum,
  TasksValueObjectsTaskStatusEnum,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import buildUrl from 'modules/core/lib/buildUrl'
import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { getModule } from 'modules/inbox_tasks/activities/Task/utils'
import { dueDateStateData, dueDateStateLabel } from 'modules/inbox_tasks/lib/dueDate'
import {
  trackClickInboxFilters,
  trackClickInboxPreset,
  trackClickInboxSorting,
  trackGoToInbox,
} from 'modules/inbox_tasks/lib/events'
import { useInboxTasksAffecteesFilter } from 'modules/inbox_tasks/lib/filters/affectee'
import {
  getTaskDescription,
  getTaskListItemAffecteeInfo,
  getTaskTitle,
  useTaskListItemUrl,
} from 'modules/inbox_tasks/lib/helpers'
import { useManualTasksEnabled } from 'modules/inbox_tasks/lib/manualTasksEnabled/useManualTasksEnabled'
import { getApprovalSuggestionKey } from 'modules/inbox_tasks/tasks/approval/TimeoffLeave/helpers'

import { TASK_KINDS, taskCategoryEnumValues } from './constants'
import { useActions } from './useActions'
import { useGrouping } from './useGrouping'
import { usePresets } from './usePresets'

const ITEMS_PER_PAGE = 20

const DATA_COLLECTION_STORAGE = { features: ['grouping' as const] }

const isTaskCategory = (category?: string): category is TasksValueObjectsTaskCategoryEnum =>
  !!category && taskCategoryEnumValues.includes(category)

interface InboxListProps {
  completed?: boolean
}

const InboxList = ({ completed }: InboxListProps) => {
  const { enabled: manualTasksEnabled, isLoading: loadingManualTasksEnabled } =
    useManualTasksEnabled()

  const { company, employee, access } = useCurrentSession()

  const trackedGoToInbox = useRef(false)

  const { getTaskListItemUrl } = useTaskListItemUrl()

  const groupingEnabled = !completed

  const [firstPageQueryData, setFirstPageQueryData] = useState<NewInboxTasksQuery>()

  const reducePresetCountForCategory = useCallback(
    (category: TasksValueObjectsTaskCategoryEnum) => {
      setFirstPageQueryData((data) =>
        data
          ? {
              ...data,
              tasks: {
                ...data.tasks,
                categoryCounts:
                  data.tasks.categoryCounts?.map((node) => ({
                    ...node,
                    count: node.count - (node.category === category ? 1 : 0),
                  })) ?? [],
              },
            }
          : undefined
      )
    },
    []
  )

  const groupingData = firstPageQueryData?.tasks.timeCounts
  const presetsData = firstPageQueryData?.tasks.categoryCounts

  const { grouping, mapData: groupingMapData } = useGrouping({
    data: groupingData,
  })

  const { bulkActions, getCachedTaskData, handleBulkAction, getTaskListItemActions } = useActions()

  const { presets, categoryFilter } = usePresets({
    taskCategoryPresets: presetsData,
  })

  const affecteeFilter = useInboxTasksAffecteesFilter({
    label: i18n.t('tasks.filter.affectee'),
    statuses: completed ? ['done'] : ['todo', 'in_progress'],
    kinds: manualTasksEnabled ? undefined : TASK_KINDS,
  })

  const dataSource = useGraphqlDataCollectionSource(
    {
      document: NewInboxTasksDocument,
      mapData: (data) => {
        const totalCount = data.tasks.inboxTasksConnection.totalCount

        if (!trackedGoToInbox.current && typeof totalCount === 'number') {
          trackGoToInbox({ type: completed ? 'completed' : 'todo', count: totalCount })
          trackedGoToInbox.current = true
        }

        if (data.tasks.categoryCounts) {
          setFirstPageQueryData(data)
        }

        return groupingMapData(data)
      },
      itemPreFilter: (item: InboxTaskListItemFragment & InboxTaskListItemCompletionFragment) => {
        const cachedTaskData = getCachedTaskData(item.id)

        const { fullName: affecteeName } = getTaskListItemAffecteeInfo(item)

        // this is to avoid showing tasks once they are marked as completed
        const isCachedTaskCompleted = cachedTaskData?.status === 'done'

        const showCompleted = isCachedTaskCompleted === !!completed

        const shouldShow =
          showCompleted && cachedTaskData?.status !== 'discarded' && !!affecteeName

        if (!shouldShow && item.category) {
          reducePresetCountForCategory(item.category)
        }

        return shouldShow
      },
      perPage: ITEMS_PER_PAGE,
      mapPagination: (data) => {
        const connection = data.tasks.inboxTasksConnection
        const pageInfo = connection.pageInfo
        const hasNextPage = !!pageInfo.hasNextPage

        const total = connection.totalCount ?? 0

        return {
          total,
          cursor: pageInfo.endCursor ?? '0',
          hasNextPage,
        }
      },
      filters: {
        affecteeSelection: affecteeFilter,
        category: categoryFilter,
      },
      itemUrl: getTaskListItemUrl,
      grouping: groupingEnabled ? grouping : undefined,
      currentGrouping: {
        field: 'creationDateGroup',
        order: 'desc',
      },
      mapVariables: ({ filters, sortings, pagination }) => {
        const sortingByCreationDate = sortings?.[0]?.field === 'creationDateGroup'
        const sortingDirection = sortings?.[0]?.order

        const isFirstPage = !pagination?.cursor

        const categoryValues = Array.isArray(filters.category) ? filters.category : []
        const categories: TasksValueObjectsTaskCategoryEnum[] = categoryValues.reduce<
          TasksValueObjectsTaskCategoryEnum[]
        >((acc, c) => {
          if (c && typeof c === 'string' && isTaskCategory(c)) {
            acc.push(c)
          }
          return acc
        }, [])

        const affecteeSelection = filters.affecteeSelection?.length
          ? { ids: filters.affecteeSelection?.map((id) => id) }
          : undefined

        const commonVariables = {
          affecteeSelection,
          categories,
          companyId: company.id,
          kinds: manualTasksEnabled ? undefined : TASK_KINDS,
          includeCategoryCount: isFirstPage,
          includeGroupingCount: false,
        }

        if (completed) {
          const statuses: TasksValueObjectsTaskStatusEnum[] = ['done']
          return {
            ...commonVariables,
            statuses,
            sortOrder: {
              field: 'completedAt',
              order: 'desc',
            },
          }
        }

        const statuses: TasksValueObjectsTaskStatusEnum[] = ['todo', 'in_progress']

        return {
          ...commonVariables,
          statuses,
          sortOrder: {
            field: sortingByCreationDate ? ('created_at' as const) : ('due_on' as const),
            order: sortingDirection ?? 'asc',
            nullsLast: !sortingByCreationDate,
          },
        }
      },
      selectable: (item: InboxTaskListItemFragment & InboxTaskListItemCompletionFragment) =>
        item.id,
      paginationType: 'infinite-scroll',
      presets,
      presetsLoading: !presetsData,
      itemActions: getTaskListItemActions,
      totalItemSummary: (totalItems) =>
        completed
          ? i18n.tp('tasks.completed_tasks', {
              count: totalItems,
            })
          : i18n.tp('tasks.pending_tasks', {
              count: totalItems,
            }),
      bulkActions,
    },
    [manualTasksEnabled]
  )

  const sortingByCreationDate = useRef(dataSource.currentGrouping?.field === 'creationDateGroup')

  useEffect(() => {
    sortingByCreationDate.current = dataSource.currentGrouping?.field === 'creationDateGroup'
  }, [dataSource.currentGrouping])

  useEffect(() => {
    if (trackedGoToInbox.current) {
      trackClickInboxSorting({
        type: dataSource.currentGrouping?.field === 'creationDateGroup' ? 'created_at' : 'due_date',
      })
    }
  }, [dataSource.currentGrouping?.field])

  const emptyStates = useMemo(
    () => ({
      'no-data': {
        emoji: '☕️',
        title: i18n.t('tasks.empty_states.no_data.title'),
        description: i18n.t('tasks.empty_states.no_data.description'),
      },
    }),
    []
  )

  const handleCaptureEvent = useCallback<ComponentProps<typeof F0EventCatcherProvider>['onEvent']>(
    (event, params) => {
      let categories: TasksValueObjectsTaskCategoryEnum[] | undefined = undefined

      if (
        params?.name === 'category' &&
        params?.value &&
        Array.isArray(params.value) &&
        !!params.value.length
      ) {
        categories = params.value.filter(
          (c): c is TasksValueObjectsTaskCategoryEnum =>
            !!c && typeof c === 'string' && isTaskCategory(c)
        )
      }

      if (event === 'datacollection.preset-click' && categories) {
        const category = categories[0]

        if (category) {
          trackClickInboxPreset({
            category,
          })
        }
      }

      if (event === 'datacollection.filter-change' && categories) {
        trackClickInboxFilters({
          categories,
        })
      }
    },
    []
  )

  const collectionId = `inbox/${completed ? 'completed' : 'todo'}/v1`

  const Collection = useMemo(
    () => (
      <OneDataCollection
        id={collectionId}
        storage={DATA_COLLECTION_STORAGE}
        source={dataSource}
        onBulkAction={handleBulkAction}
        emptyStates={emptyStates}
        visualizations={[
          {
            type: 'list',
            options: {
              fields: [
                {
                  label: i18n.t('tasks.approvals.approval_suggestion'),
                  render: (item) => {
                    const approvalSuggestion =
                      item.taskable?.__typename === 'ApprovalsMaterializedApprovalStep' &&
                      item.taskable.materializedApprovalFlow?.approvable?.__typename ===
                        'TimeoffLeave'
                        ? item.taskable.materializedApprovalFlow.approvable.approvalSuggestion
                        : undefined

                    const showApprovalSuggestion =
                      approvalSuggestion &&
                      (approvalSuggestion === 'approve' || approvalSuggestion === 'review')

                    if (!showApprovalSuggestion) {
                      return
                    }

                    const levelMap: Record<string, 'info' | 'warning' | 'critical' | 'positive'> = {
                      approve: 'positive',
                      review: 'warning',
                      reject: 'critical',
                    }

                    return {
                      type: 'alertTag' as const,
                      value: {
                        label: getApprovalSuggestionKey(approvalSuggestion),
                        level: levelMap[approvalSuggestion],
                      },
                    }
                  },
                },
                {
                  label: i18n.t('common.due_date'),
                  render: (item) => {
                    const cachedTaskData = getCachedTaskData(item.id)

                    if (cachedTaskData?.completedAt && cachedTaskData?.status === 'done') {
                      return
                    }

                    const dueDate = item.dueOn
                      ? stringToDate(item.dueOn, 'backend_datetime')
                      : undefined

                    const { state, dueDaysBetweenToday } = dueDateStateData(dueDate)

                    if (!state || (state === 'upcoming' && sortingByCreationDate.current)) return

                    if (state === 'upcoming' && dueDate && !sortingByCreationDate.current) {
                      const displayDueOn = i18nDateToString(dueDate, 'ui_date_day_month_year')

                      return {
                        type: 'tag',
                        value: {
                          label: i18n.t('tasks.due_on', { date: displayDueOn }),
                          icon: CalendarArrowRightIcon,
                        },
                      }
                    }

                    const label = dueDateStateLabel(state, dueDaysBetweenToday ?? 0)

                    return {
                      type: 'alertTag',
                      value: {
                        label,
                        icon: WarningIcon,
                        level: state === 'overdue' ? 'critical' : 'warning',
                      },
                    }
                  },
                },
                {
                  label: i18n.t('common.date.date'),
                  render: (item) => {
                    const cachedTaskData = getCachedTaskData(item.id)

                    const displayDate = i18nDateToString(
                      stringToDate(item.createdAt, 'backend_datetime'),
                      'ui_short_with_time'
                    )

                    if (cachedTaskData?.status === 'done' && cachedTaskData?.completedAt) {
                      const displayCompletedAt = i18nDateToString(
                        stringToDate(cachedTaskData.completedAt, 'backend_datetime'),
                        'ui_short_with_time'
                      )

                      return {
                        type: 'tag',
                        value: {
                          label: i18n.t('tasks.completed_on', { date: displayCompletedAt }),
                          icon: CheckDoubleIcon,
                        },
                      }
                    }

                    if (!sortingByCreationDate.current) {
                      return
                    }

                    return {
                      type: 'tag',
                      value: {
                        label: displayDate,
                        icon: CalendarArrowRightIcon,
                      },
                    }
                  },
                },
              ],
              itemDefinition: (
                item: InboxTaskListItemFragment & InboxTaskListItemCompletionFragment
              ) => {
                const groupedCount = item.groupedTasksConnection?.totalCount ?? 0
                const {
                  firstName: affecteeFirstName,
                  lastName: affecteeLastName,
                  fullName: affecteeName,
                  avatar: affecteeAvatar,
                  avatarFile,
                } = getTaskListItemAffecteeInfo(item)

                const module = getModule(item.category ?? undefined)

                const firstName = affecteeFirstName || employee.preferredName || employee.firstName
                const lastName = affecteeLastName || employee.lastName
                const avatar =
                  (item.affecteesConnection.nodes.length
                    ? affecteeAvatar
                    : buildUrl(access.avatar)) ?? undefined

                const title = getTaskTitle({
                  taskName: item.name,
                  taskableInfoForTitle: item.taskable,
                  affecteeName,
                  groupedCount,
                  dueOn: item.dueOn ?? undefined,
                })

                let description = getTaskDescription({
                  taskableInfoForDescription: item.taskable,
                })

                if (!description?.length && item.category) {
                  description = []
                }

                const avatarParams =
                  groupedCount > 1
                    ? {
                        firstName: groupedCount.toString(),
                        lastName: '',
                        src: undefined,
                      }
                    : {
                        type: 'person' as const,
                        firstName,
                        lastName,
                        src: avatar,
                      }

                return {
                  title: title ?? item.name,
                  description,
                  avatar: {
                    ...(avatarFile
                      ? {
                          type: 'file' as const,
                          file: {
                            name: avatarFile.filename,
                            type: avatarFile.extension ?? '',
                          },
                        }
                      : {
                          type: 'person' as const,
                          ...avatarParams,
                        }),
                    badge: module
                      ? {
                          type: 'module',
                          module: module.id,
                        }
                      : undefined,
                  },
                }
              },
            },
          },
        ]}
        fullHeight
      />
    ),
    [
      collectionId,
      dataSource,
      emptyStates,
      employee.firstName,
      employee.lastName,
      employee.preferredName,
      access.avatar,
      handleBulkAction,
      getCachedTaskData,
    ]
  )

  const loading = loadingManualTasksEnabled

  if (loading) {
    return <Loading />
  }

  return <F0EventCatcherProvider onEvent={handleCaptureEvent}>{Collection}</F0EventCatcherProvider>
}

export default observer(InboxList)
